import type ConstructorIOClient from '@constructor-io/constructorio-client-javascript'
import {
  CONSTRUCTOR_API_BASE,
  getConstructorIndexKey,
} from '@material-bank/mtag-constructor'
import type { ConstructorUserContext } from './constructorUserContextQuery'

type ConstructorSdk =
  typeof import('@constructor-io/constructorio-client-javascript')

let sdk: Promise<ConstructorSdk> | null = null
let cached: { key: string; client: Promise<ConstructorIOClient> } | null = null

// Imported on first use so the SDK stays out of the PDP's initial bundle. A
// failed import isn't kept, so the next request tries again.
function loadConstructorSdk(): Promise<ConstructorSdk> {
  if (!sdk) {
    const pending = import('@constructor-io/constructorio-client-javascript')
    sdk = pending
    pending.catch(() => {
      if (sdk === pending) {
        sdk = null
      }
    })
  }

  return sdk
}

// Starts the download before the caller is known, so it overlaps the user
// context request instead of following it.
export function preloadConstructorSdk(): void {
  loadConstructorSdk().catch(() => undefined)
}

// The SDK reads userId and segments off the instance at request time, so a new
// instance is needed whenever the locale or the caller changes — otherwise a
// client built for a guest keeps sending `us=anon` after sign-in. The client and
// session ids (`i`, `s`) are the SDK's own and survive the rebuild.
export function getConstructorBrowserClient(
  locale: string,
  userContext: ConstructorUserContext,
): Promise<ConstructorIOClient> {
  const userId = userContext.isMember
    ? userContext.userId ?? undefined
    : undefined
  const segment = userContext.isMember ? 'member' : 'anon'
  const key = [locale, userId ?? '', segment].join('|')

  if (cached?.key === key) {
    return cached.client
  }

  const client = loadConstructorSdk().then(
    ({ default: ConstructorIO }) =>
      new ConstructorIO({
        apiKey: getConstructorIndexKey(locale),
        serviceUrl: CONSTRUCTOR_API_BASE,
        userId,
        segments: [segment],
      }),
  )

  const entry = { key, client }
  cached = entry

  // Don't keep a failed import or a missing index key around; the next request
  // tries again.
  client.catch(() => {
    if (cached === entry) {
      cached = null
    }
  })

  return client
}

export function resetConstructorBrowserClient(): void {
  sdk = null
  cached = null
}
